import { useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { Nfc, X } from "lucide-react";
import { accountDisplayName } from "../lib/accountDisplayName";

/**
 * Premium Digital iCaard hero — mockup-faithful redesign (Enterprise P2).
 * Deep navy → royal purple → black gradient, gold NFC symbol, LARGE EC-H QR,
 * subtle Ghana accent stripe. Tapping opens the full card page.
 */
export default function DigitalCardHero({ user, card }) {
  const [openScan, setOpenScan] = useState(false);
  const qrValue = card?.qr_code || user?.digital_card_number || "";
  const cardNo = user?.digital_card_number || card?.digital_card_number || "";
  const expiry = (card?.expiry_date || "").slice(0, 10);
  const displayName = accountDisplayName(user);

  return (
    <div onClick={() => setOpenScan(true)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenScan(true); } }} role="button" tabIndex={0} aria-label="Open your iCaard QR code" data-testid="digital-card-hero"
         className="relative cursor-pointer rounded-3xl overflow-hidden border border-[#26263A] shadow-2xl shadow-black/50 transition-transform hover:scale-[1.01]"
         style={{ background: "linear-gradient(120deg, #142E3A, #183D43 65%, #176454)" }}>
      {/* soft glow */}
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#6D28FF]/25 blur-3xl pointer-events-none" />
      <div className="icaard-bank-card-content relative p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icaard-logo-transparent.png" alt="iCaard" className="h-7 w-auto" />
          </div>
          <Nfc size={22} className="text-[#F5B400]" data-testid="card-nfc-icon" />
        </div>

        <div className="icaard-bank-card-details mt-4 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-lg sm:text-xl text-white truncate" data-testid="card-hero-name">{displayName}</div>
            <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] bg-[#F5B400]/15 text-[#F5B400] border border-[#F5B400]/40">
              {(user?.role || "member").replace(/_/g, " ")}
            </span>
            {cardNo && <div className="mt-2 font-mono text-[11px] tracking-[0.15em] text-[#B4B4C7] truncate" data-testid="card-hero-number">{cardNo}</div>}
            {expiry && <div className="text-[9px] text-[#6F7285] mt-0.5">VALID THRU {expiry}</div>}
          </div>
          {/* LARGE, instantly scannable QR — EC level H */}
          {qrValue && (
            <div className="bg-white rounded-2xl p-2.5 flex-shrink-0 shadow-lg" data-testid="card-hero-qr">
              <QRCodeSVG value={qrValue} size={80} level="H" includeMargin={false}
                         imageSettings={{ src: "/icard-logo.png", height: 14, width: 14, excavate: true }} />
            </div>
          )}
        </div>

        <div className="icaard-bank-card-footer mt-4 flex items-center justify-between">
          <span className="text-[9px] tracking-[0.28em] uppercase font-bold text-[#B4B4C7]">Your everyday card</span>
          <span className="text-[9px] text-[#6F7285]">Tap to scan</span>
        </div>
      </div>
      {/* Ghana accent stripe */}
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #CE1126 0%, #F5B400 50%, #006B3F 100%)" }} />

      {/* Scan mode — BIG QR + name + card number only, for instant scanning */}
      {openScan && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-5 bg-black/90 backdrop-blur-sm cursor-default"
             data-testid="card-scan-modal"
             onClick={(e) => { e.stopPropagation(); setOpenScan(false); }}>
          <button data-testid="card-scan-close" aria-label="Close"
                  className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                  onClick={(e) => { e.stopPropagation(); setOpenScan(false); }}>
            <X size={20} />
          </button>
          <div className="text-center w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
            {qrValue && (
              <div className="bg-white rounded-3xl p-5 mx-auto inline-block shadow-2xl" data-testid="card-scan-qr">
                <QRCodeSVG value={qrValue} size={Math.min(300, Math.floor(window.innerWidth * 0.72))} level="H" includeMargin={false}
                           imageSettings={{ src: "/icard-logo.png", height: 36, width: 36, excavate: true }} />
              </div>
            )}
            <div className="mt-5 font-display font-black text-2xl text-white" data-testid="card-scan-name">{displayName}</div>
            {cardNo && (
              <div className="mt-1.5 font-mono text-sm tracking-[0.2em] text-[#F5B400]" data-testid="card-scan-number">{cardNo}</div>
            )}
            <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#9CA3D4]">Hold steady · Tap anywhere to close</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
